import AHDate from "_shared/dates/date";
import useTimelineIdleFade, { type TimelineIdleFade } from "_shared/_views/timelines/use-timeline-idle-fade";
import { useCallback, useState } from "react";
import type Comment from "work/proposals/comments/comment";

type CommentTimelineInput = {
  /** Every comment the pane lists, from each conversation it shows. */
  comments: readonly Comment[];
  /**
   * A comment the page wants on screen: the oldest unread one, when "Prev / Next" arrives at the
   * unread comments. A date filter that would hide it is dropped, so the arrival is never a no-op.
   */
  revealCommentId?: string;
};

export type CommentTimeline = {
  /** The span the scrubber's top level covers: the oldest and newest comment the pane holds. */
  firstDate: AHDate | undefined;
  lastDate: AHDate | undefined;
  /** Changes each time the scrubber opens afresh; key the scrubber on it to start it over. */
  generation: number;
  /** The scrubber is on screen. */
  show: boolean;
  /** A date the scrubber picked is filtering the pane. */
  filtering: boolean;
  /** The range the pane is filtered to, while it is. */
  filteredTo: DateRange | undefined;
  fade: TimelineIdleFade;
  /** Whether the pane lists a comment under the current filter. */
  includes: (comment: Comment) => boolean;
  /** The pane's date toggle: opens the scrubber, or stops filtering and closes it. */
  toggle: () => void;
  /** The user grabbed the pane's scrollbar. */
  openFromScrollbar: () => void;
  /** The scrubber picked a range, or, with no dates, asked to close. */
  selectDates: (startDate?: string, endDate?: string) => void;
  /** The scrubber went back to its top level. */
  reset: () => void;
};

export type DateRange = { start: AHDate; end: AHDate };

type OpenedBy = "scrollbar" | "toggle";

/**
 * The timeline scrubber over a proposal's Correspondence pane (#5695).
 *
 * The messages pane asks the server for a date-filtered page; this pane already holds every comment of
 * the negotiation in the store, so a pick filters what is listed and asks for nothing. That also makes
 * the range simpler than the messages pane's: it is the oldest and newest comment the store holds,
 * filtered or not, and a comment that arrives by realtime widens it on the next render.
 *
 * A filter is a narrower view of a conversation people are negotiating in, so it gives way whenever it
 * would hide something the reader is meant to see: the comment the reader has just posted, and the
 * unread comment "Prev / Next" has just brought them to.
 */
export default function useCommentTimeline(input: Readonly<CommentTimelineInput>): CommentTimeline {
  const { comments, revealCommentId } = input;
  const [openedBy, setOpenedBy] = useState<OpenedBy>();
  const [dateRange, setDateRange] = useState<DateRange>();
  const [generation, setGeneration] = useState(0);

  const close = useCallback(() => setOpenedBy(undefined), []);
  const fade = useTimelineIdleFade({ enabled: openedBy === "scrollbar", onFaded: close });
  const { firstDate, lastDate } = spanOf(comments);

  function open(by: OpenedBy) {
    // A scrubber closed with its filter still applied opens again where it was left, Back and all.
    if (!openedBy && !dateRange) setGeneration(generation + 1);
    setOpenedBy(by);
  }

  function clear() {
    setDateRange(undefined);
    setOpenedBy(undefined);
  }

  // Adjusting state while rendering, React's pattern for reacting to a changed input without an effect.
  // A comment that has not entered the conversation yet is the one the reader just posted, still on the
  // wire; once the server answers it would fall outside most filters and vanish from under its author.
  const hasPostedComment = comments.some((comment) => !comment.enteredConversationAt);
  const [sawPostedComment, setSawPostedComment] = useState(hasPostedComment);
  if (hasPostedComment !== sawPostedComment) {
    setSawPostedComment(hasPostedComment);
    if (hasPostedComment && dateRange) clear();
  }

  const [revealedCommentId, setRevealedCommentId] = useState(revealCommentId);
  if (revealCommentId !== revealedCommentId) {
    setRevealedCommentId(revealCommentId);
    const target = comments.find((comment) => comment.id?.value === revealCommentId);
    if (target && dateRange && !isWithin(target, dateRange)) clear();
  }

  return {
    firstDate,
    lastDate,
    generation,
    show: openedBy !== undefined,
    filtering: dateRange !== undefined,
    filteredTo: dateRange,
    fade,
    includes: (comment) => !dateRange || isWithin(comment, dateRange),
    toggle: () => {
      if (dateRange) clear();
      else open("toggle");
    },
    // A scrubber already open with its toggle stays a toggled one, and does not fade.
    openFromScrollbar: () => {
      if (openedBy) fade.noteActivity();
      else open("scrollbar");
    },
    selectDates: (startDate, endDate) => {
      const start = startDate ? new AHDate(startDate) : undefined;
      const end = endDate ? new AHDate(endDate) : undefined;
      if (!start || !end) {
        close();
        return;
      }
      // A pick is the plainest sign the user wants the scrubber, however it came up.
      fade.engage();
      setDateRange({ start, end });
    },
    reset: () => {
      fade.engage();
      setDateRange(undefined);
    },
  };
}

// A comment still on the wire has no moment yet, and is listed whatever the filter.
function isWithin(comment: Comment, { start, end }: DateRange): boolean {
  const moment = comment.enteredConversationAt;
  return !moment || (!moment.isBefore(start) && moment.isSameOrBefore(end));
}

function spanOf(comments: readonly Comment[]): { firstDate?: AHDate; lastDate?: AHDate } {
  let firstDate: AHDate | undefined;
  let lastDate: AHDate | undefined;
  for (const comment of comments) {
    const moment = comment.enteredConversationAt;
    if (!moment) continue;
    if (!firstDate || moment.isBefore(firstDate)) firstDate = moment;
    if (!lastDate || moment.isAfter(lastDate)) lastDate = moment;
  }
  return { firstDate, lastDate };
}
